import React, { useEffect, useState, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { useProfile } from '../contexts/ProfileContext'
import { useBarista } from '../contexts/BaristaContext'
import BaristaLoginModal from '../components/BaristaLoginModal'
import { getBranchStaffContext, getStaffContext } from '../offline/posOfflineHelpers'

export default function StaffSidebar({
    staffData,
    sidebarOpen,
    setSidebarOpen,
    showLogout,
    setShowLogout,
    activeMenu,
    setActiveMenu,
    onLogoutClick,
}) {
    const { profilePicture, adminUsername, adminRole, updateProfilePicture, updateAdminInfo } = useProfile()
    const { selectedBarista, setSelectedBarista, branchBaristas, loadBaristas } = useBarista()
    const [switchTarget, setSwitchTarget] = useState(null)
    const [baristaDropdownOpen, setBaristaDropdownOpen] = useState(false)
    const navScrollRef = useRef(null)
    const [isDraggingNav, setIsDraggingNav] = useState(false)
    const navDragStartYRef = useRef(0)
    const navScrollStartRef = useRef(0)
    const isBarista = staffData?.role === 'Barista'

    useEffect(() => {
        const fetchStaffProfile = async () => {
            if (!staffData?.id) return
                try {
                    updateAdminInfo(
                        staffData.branches?.name || 'Staff',
                        staffData.role || 'Staff'
                    )

                    const { data: profileData } = await supabase
                        .from('profiles')
                        .select('profile_picture_url')
                        .eq('user_id', staffData.id)
                        .eq('user_type', 'staff')
                        .maybeSingle()

                    if (profileData?.profile_picture_url) {
                        updateProfilePicture(profileData.profile_picture_url)
                    }
                } catch (err) {
                    console.error('Error fetching staff profile picture:', err)
                }
            }
        fetchStaffProfile()
    }, [staffData?.id])

    useEffect(() => {
        console.log('staffData.branch_id:', staffData?.branch_id, 'isBarista:', isBarista)
        if (isBarista && staffData?.branch_id) loadBaristas(staffData.branch_id)
    }, [staffData?.branch_id, isBarista, loadBaristas])

    // useEffect(() => {
    //     if (isBarista && staffData?.branch_id) loadBaristas(staffData.branch_id)
    // }, [staffData?.branch_id, isBarista, loadBaristas])

    useEffect(() => {
        if (!staffData?.id) return
        getBranchStaffContext(true).catch(err =>
            console.error('Failed to cache branch/staff context:', err)
        )
        getStaffContext(true).catch(err =>
            console.error('Failed to cache staff context:', err)
        )
    }, [staffData?.id])

    const getMenuItems = () => {
        if (!staffData) return []
        const { permissions } = staffData
        const items = []

        items.push({
            name: 'Dashboard Overview',
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M13 9V3h8v6zM3 13V3h8v10zm10 8V11h8v10zM3 21v-6h8v6zm2-10h4V5H5zm10 8h4v-6h-4zm0-12h4V5h-4zM5 19h4v-2H5zm4-2"/></svg>
        })

        if (permissions.canViewAnalytics && staffData.role === 'Admin') items.push({
            name: 'Business Analytics',
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><path fill="currentColor" d="M4.329 9.433a.4.4 0 0 1 .4.4v4.668a.4.4 0 0 1-.4.4H1.53a.4.4 0 0 1-.402-.4V9.833c0-.22.18-.4.402-.4zM1.933 14.1h1.994v-3.868H1.933zm7.495-13a.4.4 0 0 1 .401.4v13c0 .22-.18.4-.4.4H6.63a.4.4 0 0 1-.402-.4v-13c0-.22.18-.4.402-.4zm-2.395 13h1.993V1.9H7.033zm7.495-9.437a.4.4 0 0 1 .4.4V14.5a.4.4 0 0 1-.4.4H11.73a.4.4 0 0 1-.402-.4V5.063c0-.22.18-.4.402-.4zm-2.396 9.438h1.994V5.464h-1.994z" clipRule="evenodd"/></svg>
        })

        if (permissions.canAccessCustomerData && staffData.role === 'Admin') items.push({
            name: 'Customer Insight',
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15"><path fill="currentColor" d="M5 8.9c1.44 0 2.68.252 3.575.855C9.502 10.38 10 11.343 10 12.6a.501.501 0 0 1-1 0c0-.958-.358-1.596-.983-2.017C7.359 10.141 6.35 9.9 5 9.9s-2.36.241-3.017.684C1.358 11.005 1 11.643 1 12.601a.501.501 0 0 1-1 0c0-1.258.497-2.221 1.424-2.846C2.319 9.152 3.56 8.9 5 8.9m4.975 0c1.439 0 2.68.252 3.575.855c.927.625 1.425 1.588 1.425 2.846a.5.5 0 0 1-1 0c0-.958-.358-1.596-.984-2.017c-.518-.349-1.253-.57-2.202-.65a4.5 4.5 0 0 0-.87-1.033zM5 1.85a3.151 3.151 0 0 1 0 6.3a3.15 3.15 0 1 1 0-6.3m4.975 0a3.15 3.15 0 0 1 0 6.3c-.524 0-1.016-.13-1.45-.356a4.5 4.5 0 0 0 .534-.852a2.15 2.15 0 1 0 0-3.887a4.5 4.5 0 0 0-.535-.85a3.1 3.1 0 0 1 1.45-.355M5 2.85a2.151 2.151 0 0 0 0 4.3a2.15 2.15 0 0 0 0-4.3"/></svg>
        })

        items.push({ name: 'Loyalty Management',
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12"><path fill="currentColor" d="M2.25 1C1.56 1 1 1.56 1 2.25v1.162a1.5 1.5 0 0 0 .772 1.31l2.876 1.599a3 3 0 1 0 2.704 0l2.877-1.598A1.5 1.5 0 0 0 11 3.412V2.25C11 1.56 10.44 1 9.75 1zM2 2.25A.25.25 0 0 1 2.25 2H4v2.817l-1.743-.968A.5.5 0 0 1 2 3.412zm3 3.122V2h2v3.372l-1 .556zm3-.555V2h1.75a.25.25 0 0 1 .25.25v1.162a.5.5 0 0 1-.257.437zM8 9a2 2 0 1 1-4 0a2 2 0 0 1 4 0"/></svg>
        })

        if (staffData.role === 'Admin') items.push({
            name: 'Menu Management',
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M3 2h2v20H3zm7 4h7v2h-7zm0 4h7v2h-7z"/><path fill="currentColor" d="M19 2H6v20h13c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2m0 18H8V4h11z"/></svg>
        })

        if (permissions.canManageOrders) items.push({
            name: 'Order Management',
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><g fill="none" stroke="currentColor" strokeWidth="4"><path strokeLinejoin="round" d="M6 15h36l-2 27H8z" clipRule="evenodd"/><path strokeLinecap="round" strokeLinejoin="round" d="M16 19V6h16v13"/><path strokeLinecap="round" d="M16 34h16"/></g></svg>
        })

        if (permissions.canModifyInventory) items.push({
            name: 'Inventory Management',
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path fill="currentColor" d="M26.321 4.832a6.25 6.25 0 0 0-4.642 0L6.672 10.835A4.25 4.25 0 0 0 4 14.78v18.438a4.25 4.25 0 0 0 2.672 3.945l15.007 6.003a6.25 6.25 0 0 0 4.642 0l15.007-6.003A4.25 4.25 0 0 0 44 33.219V14.781a4.25 4.25 0 0 0-2.672-3.946zm-3.714 2.321a3.75 3.75 0 0 1 2.786 0l13.991 5.597l-6.009 2.403L17.991 9zm-7.982 3.193L30.009 16.5L24 18.903L8.616 12.75zM6.509 14.6l16.241 6.496V40.9l-.143-.054L7.6 34.843a1.75 1.75 0 0 1-1.1-1.625V14.781q0-.092.01-.181m18.74 26.3V21.096L41.49 14.6q.01.09.01.18v18.438a1.75 1.75 0 0 1-1.1 1.625l-15.007 6.003z"/></svg>
        })

        if (staffData.role === 'Barista') items.push({
            name: 'POS System',
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M2 20h20v2H2zm2-8h2v7H4zm5 0h2v7H9zm4 0h2v7h-2zm5 0h2v7h-2zM2 7l10-5l10 5v4H2zm2 1.236V10h16V8.236l-8-4zM12 8a1 1 0 1 0 0-2a1 1 0 0 0 0 2"/></svg>
        })

        items.push({
            name: 'Transaction Printer',
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M18 3H6v4H5a3 3 0 0 0-3 3v6a2 2 0 0 0 2 2h2v3h12v-3h2a2 2 0 0 0 2-2v-6a3 3 0 0 0-3-3zM8 5h8v2H8zm0 14v-4h8v4zm10-7a1 1 0 1 1 0-2a1 1 0 0 1 0 2z"/></svg>
        })

        items.push({
            name: 'Shift Attendance',
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8s8 3.59 8 8s-3.59 8-8 8m.5-13H11v6l5.25 3.15l.75-1.23l-4.5-2.67z"/></svg>
        })

        if (staffData.role === 'Admin') items.push({
            name: 'Staff Management',
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="m16.719 19.752l-.64-5.124A3 3 0 0 0 13.101 12h-2.204a3 3 0 0 0-2.976 2.628l-.641 5.124A2 2 0 0 0 9.266 22h5.468a2 2 0 0 0 1.985-2.248"/><circle cx="12" cy="5" r="3"/><circle cx="4" cy="9" r="2"/><circle cx="20" cy="9" r="2"/><path strokeLinecap="round" strokeLinejoin="round" d="M4 14h-.306a2 2 0 0 0-1.973 1.671l-.333 2A2 2 0 0 0 3.361 20H7m13-6h.306a2 2 0 0 1 1.973 1.671l.333 2A2 2 0 0 1 20.639 20H17"/></g></svg>
        })
        // if (staffData.role === 'Admin') items.push({
        //     name: 'Branch Management',
        //     icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 12c.263 0 .524-.06.767-.175a2 2 0 0 0 .65-.491c.186-.21.333-.46.433-.734s.15-.568.15-.864a2.4 2.4 0 0 0 .586 1.591c.375.422.884.659 1.414.659s1.04-.237 1.414-.659A2.4 2.4 0 0 0 12 9.736a2.4 2.4 0 0 0 .586 1.591c.375.422.884.659 1.414.659s1.04-.237 1.414-.659A2.4 2.4 0 0 0 16 9.736c0 .295.052.588.152.861s.248.521.434.73a2 2 0 0 0 .649.488a1.8 1.8 0 0 0 1.53 0a2 2 0 0 0 .65-.488c.185-.209.332-.457.433-.73s.152-.566.152-.861c0-.974-1.108-3.85-1.618-5.121A.98.98 0 0 0 17.466 4H6.456a.99.99 0 0 0-.93.645C5.045 5.962 4 8.905 4 9.736c.023.59.241 1.148.611 1.567c.37.418.865.667 1.389.697m0 0c.328 0 .651-.091.94-.266A2.1 2.1 0 0 0 7.66 11h.681a2.1 2.1 0 0 0 .718.734c.29.175.613.266.942.266s.651-.091.94-.266c.29-.174.537-.427.719-.734h.681a2.1 2.1 0 0 0 .719.734c.289.175.612.266.94.266c.329 0 .652-.091.942-.266c.29-.174.536-.427.718-.734h.681c.183.307.43.56.719.734c.29.174.613.266.941.266a1.8 1.8 0 0 0 1.06-.351M6 12a1.77 1.77 0 0 1-1.163-.476M5 12v7a1 1 0 0 0 1 1h2v-5h3v5h7a1 1 0 0 0 1-1v-7m-5 3v2h2v-2z"/></svg>
        // })
        
        return items
    }

    const handleNavMouseDown = (e) => {
        if (!navScrollRef.current) return
        setIsDraggingNav(true)
        navDragStartYRef.current = e.pageY - navScrollRef.current.offsetTop
        navScrollStartRef.current = navScrollRef.current.scrollTop
    }

    const handleNavMouseMove = (e) => {
        if (!isDraggingNav || !navScrollRef.current) return
        e.preventDefault()
        const y = e.pageY - navScrollRef.current.offsetTop
        const walk = y - navDragStartYRef.current
        navScrollRef.current.scrollTop = navScrollStartRef.current - walk
    }

    const handleNavMouseUp = () => setIsDraggingNav(false)
    const handleNavMouseLeave = () => setIsDraggingNav(false)

    const menuItems = getMenuItems()
    const { role, branches } = staffData || {}

    const handleProfileClick = () => {
        if (isBarista) return
        setShowLogout(false)
        setActiveMenu('My Profile')
    }
    const handleSettingsClick = () => { setShowLogout(false); setActiveMenu('Settings') }
    
    return (
        <aside className={`${
            sidebarOpen
                ? 'w-20 relative'
                : 'fixed inset-0 z-50 w-full sm:relative sm:inset-auto sm:z-auto sm:w-72'
        } bg-[#303030] text-white transition-all duration-300 flex flex-col shadow-2xl`}>
                {/* Logo / toggle */}
                <div className={`p-6 flex items-center border-b border-[#474747] ${sidebarOpen ? 'justify-center' : 'justify-between'}`}>
                    <h1 className={`text-2xl font-semibold transition-all duration-300 ${sidebarOpen ? 'hidden' : 'block'}`}>
                        Staff Portal
                    </h1>
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="text-white hover:bg-[#FF6F3C] rounded p-1 flex-shrink-0">
                        {sidebarOpen ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path fill="currentColor" d="M4 18q-.425 0-.712-.288T3 17t.288-.712T4 16h16q.425 0 .713.288T21 17t-.288.713T20 18zm0-5q-.425 0-.712-.288T3 12t.288-.712T4 11h16q.425 0 .713.288T21 12t-.288.713T20 13zm0-5q-.425 0-.712-.288T3 7t.288-.712T4 6h16q.425 0 .713.288T21 7t-.288.713T20 8z"/>
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path fill="currentColor" d="M6.4 19L5 17.6l5.6-5.6L5 6.4L6.4 5l5.6 5.6L17.6 5L19 6.4L13.4 12l5.6 5.6l-1.4 1.4l-5.6-5.6z"/>
                            </svg>
                        )}
                    </button>
                </div>

                {/* Barista selector — only for Barista role accounts, only when expanded */}
                {isBarista && !sidebarOpen && (
                    <div className="px-4 py-3 border-b border-[#474747] relative">
                        <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5 font-medium">Serving as</p>
                        <button
                            onClick={() => setBaristaDropdownOpen(!baristaDropdownOpen)}
                            className="w-full flex items-center justify-between gap-2 bg-[#3e3e3e] hover:bg-[#4a4a4a] border border-[#555] rounded-lg px-3 py-2 transition-colors">
                            <div className="flex items-center gap-2 min-w-0">
                                <span className={`text-sm truncate ${selectedBarista ? 'text-white' : 'text-white/40'}`}>
                                    {selectedBarista ? `${selectedBarista.first_name} ${selectedBarista.last_name}` : 'Select your name…'}
                                </span>
                            </div>
                        </button>

                        {baristaDropdownOpen && (
                            <div className="absolute left-4 right-4 top-full mt-1 z-50 bg-[#2a2a2a] border border-[#555] rounded-lg shadow-xl overflow-hidden">
                                {branchBaristas.length === 0 ? (
                                    <p className="px-4 py-3 text-sm text-white/40 text-center">No active baristas found</p>
                                ) : (
                                    branchBaristas.map((barista) => (
                                        <button
                                            key={barista.barista_id}
                                                onClick={() => { setSwitchTarget(barista); setBaristaDropdownOpen(false) }}                                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors text-sm ${
                                                selectedBarista?.barista_id === barista.barista_id
                                                    ? 'bg-[#C9B5A0] text-[#303030] font-medium'
                                                    : 'text-white hover:bg-[#3e3e3e]'
                                            }`}>
                                            <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                                {barista.first_name[0]}{barista.last_name[0]}
                                            </span>
                                            {barista.first_name} {barista.last_name}
                                            
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Collapsed barista indicator */}
                {isBarista && sidebarOpen && selectedBarista && (
                    <div className="flex justify-center py-2 border-b border-[#474747]">
                        <div title={`${selectedBarista.first_name} ${selectedBarista.last_name}`}
                            className="w-8 h-8 rounded-full bg-[#C9B5A0] flex items-center justify-center text-[#303030] text-xs font-bold">
                            {selectedBarista.first_name[0]}{selectedBarista.last_name[0]}
                        </div>
                    </div>
                )}

                {/* Nav items */}
                <nav
                    ref={navScrollRef}
                    onMouseDown={handleNavMouseDown}
                    onMouseMove={handleNavMouseMove}
                    onMouseUp={handleNavMouseUp}
                    onMouseLeave={handleNavMouseLeave}
                    className={`sidebar-nav flex-1 overflow-y-auto py-4 select-none ${isDraggingNav ? 'cursor-grabbing' : 'cursor-grab'}`}
                >
                    {menuItems.map((item, index) => (
                        <button
                            key={index}
                            onClick={() => setActiveMenu(item.name)}
                            className={`w-full flex items-center gap-3 py-4 text-left transition-colors ${
                                activeMenu === item.name ? 'bg-[#C9B5A0]' : 'hover:bg-[#8B6F5F]'
                            } ${sidebarOpen ? 'justify-center px-0' : 'px-6'}`}
                            title={sidebarOpen ? item.name : ''}>
                            <span className="flex-shrink-0 w-6 h-6">{item.icon}</span>
                            <span className={`text-base transition-all duration-300 ${sidebarOpen ? 'hidden' : 'block'}`}>
                                {item.name}
                            </span>
                        </button>
                    ))}
                </nav>

                {/* User profile area */}
                <div className={`border-t border-[#474747] relative ${sidebarOpen ? 'flex justify-center' : ''}`}>

                    {/* Popover menu */}
                    {showLogout && !sidebarOpen && (
                        <div className="absolute bottom-full left-0 right-0 mb-1 mx-4 bg-white rounded-lg shadow-lg overflow-hidden">
                            {!isBarista && (
                                <button onClick={handleProfileClick}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                        <path fill="currentColor" d="M12 4a4 4 0 0 1 4 4a4 4 0 0 1-4 4a4 4 0 0 1-4-4a4 4 0 0 1 4-4m0 10c4.42 0 8 1.79 8 4v2H4v-2c0-2.21 3.58-4 8-4"/>
                                    </svg>
                                    <span className="text-sm font-medium">My Profile</span>
                                </button>
                            )}
                            <button onClick={handleSettingsClick}
                                className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.3 7.3 0 0 0-1.63-.94L14.4 2.7a.5.5 0 0 0-.49-.4h-3.82a.5.5 0 0 0-.49.4l-.36 2.62c-.58.22-1.12.53-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.7 8.84a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94L2.82 14.52a.5.5 0 0 0-.12.64l1.92 3.32a.5.5 0 0 0 .6.22l2.39-.96c.5.41 1.05.72 1.63.94l.36 2.62a.5.5 0 0 0 .49.4h3.82a.5.5 0 0 0 .49-.4l.36-2.62c.58-.22 1.12-.53 1.63-.94l2.39.96a.5.5 0 0 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64zM12 15.5A3.5 3.5 0 1 1 12 8a3.5 3.5 0 0 1 0 7.5"/>
                                </svg>
                                <span className="text-sm font-medium">Settings</span>
                            </button>
                            <button onClick={onLogoutClick}
                                className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M5 21q-.825 0-1.412-.587T3 19V5q0-.825.588-1.412T5 3h6q.425 0 .713.288T12 4t-.288.713T11 5H5v14h6q.425 0 .713.288T12 20t-.288.713T11 21zm12.175-8H10q-.425 0-.712-.288T9 12t.288-.712T10 11h7.175L15.3 9.125q-.275-.275-.275-.675t.275-.7t.7-.313t.725.288L20.3 11.3q.3.3.3.7t-.3.7l-3.575 3.575q-.3.3-.712.288t-.713-.313q-.275-.3-.262-.712t.287-.688z"/>
                                </svg>
                                <span className="text-sm font-medium">Logout</span>
                            </button>
                        </div>
                    )}

                    <div className="p-6">
                        {sidebarOpen ? (
                            <button
                                onClick={handleProfileClick}
                                className={`w-10 h-10 rounded-full bg-white/20 flex items-center justify-center cursor-pointer hover:bg-white/30 transition-colors overflow-hidden ${activeMenu === 'My Profile' ? 'bg-white/40' : ''}`}
                                title={`${branches?.name || 'Staff'} — ${adminRole || role}`}>
                                {profilePicture
                                    ? <img src={profilePicture} alt="Profile" className="w-full h-full object-cover"/>
                                    : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" fillRule="evenodd" d="M12 4a8 8 0 0 0-6.96 11.947A4.99 4.99 0 0 1 9 14h6a4.99 4.99 0 0 1 3.96 1.947A8 8 0 0 0 12 4m7.943 14.076q.188-.245.36-.502A9.96 9.96 0 0 0 22 12c0-5.523-4.477-10-10-10S2 6.477 2 12a9.96 9.96 0 0 0 2.057 6.076l-.005.018l.355.413A9.98 9.98 0 0 0 12 22q.324 0 .644-.02a9.95 9.95 0 0 0 5.031-1.745a10 10 0 0 0 1.918-1.728l.355-.413zM12 6a3 3 0 1 0 0 6a3 3 0 0 0 0-6" clipRule="evenodd"/></svg>
                                }
                            </button>
                        ) : (
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleProfileClick}
                                    className={`w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 hover:bg-white/30 transition-colors overflow-hidden ${activeMenu === 'My Profile' ? 'bg-white/40' : ''}`}>
                                    {profilePicture
                                        ? <img src={profilePicture} alt="Profile" className="w-full h-full object-cover"/>
                                        : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" fillRule="evenodd" d="M12 4a8 8 0 0 0-6.96 11.947A4.99 4.99 0 0 1 9 14h6a4.99 4.99 0 0 1 3.96 1.947A8 8 0 0 0 12 4m7.943 14.076q.188-.245.36-.502A9.96 9.96 0 0 0 22 12c0-5.523-4.477-10-10-10S2 6.477 2 12a9.96 9.96 0 0 0 2.057 6.076l-.005.018l.355.413A9.98 9.98 0 0 0 12 22q.324 0 .644-.02a9.95 9.95 0 0 0 5.031-1.745a10 10 0 0 0 1.918-1.728l.355-.413zM12 6a3 3 0 1 0 0 6a3 3 0 0 0 0-6" clipRule="evenodd"/></svg>
                                    }
                                </button>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">
                                        {branches?.name || 'Staff'}
                                    </p>
                                    <p className="text-xs text-white/70">{adminRole || role}</p>
                                </div>
                                <button
                                    onClick={() => setShowLogout(!showLogout)}
                                    className="hover:bg-white/10 rounded p-1 transition-colors">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16"
                                        className={`transition-transform duration-200 ${showLogout ? 'rotate-180' : ''}`}>
                                        <path fill="currentColor" fillRule="evenodd" d="m8 10.207l3.854-3.853l-.707-.708L8 8.793L4.854 5.646l-.708.708z" clipRule="evenodd"/>
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                {switchTarget && (
                    <BaristaLoginModal
                        presetBarista={switchTarget}
                        onSuccess={() => setSwitchTarget(null)}
                        onCancel={() => setSwitchTarget(null)}
                    />
                )}

                <style jsx>{`
                    .sidebar-nav::-webkit-scrollbar {
                        width: 6px;
                    }
                    .sidebar-nav::-webkit-scrollbar-track {
                        background: rgba(255, 255, 255, 0.1);
                        border-radius: 10px;
                    }
                    .sidebar-nav::-webkit-scrollbar-thumb {
                        background: #A0826D;
                        border-radius: 10px;
                    }
                    .sidebar-nav::-webkit-scrollbar-thumb:hover {
                        background: #8B6F5F;
                    }
                    .sidebar-nav {
                        scrollbar-width: thin;
                        scrollbar-color: #474747 rgba(255, 255, 255, 0.1);
                    }
                `}</style>
            </aside>

  )

}